(() => {
  const selector = '[data-ps-pdp-standard]';
  const initialized = new WeakSet();
  const locale = document.documentElement.lang || 'pt-BR';
  const decimal = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  const svgNs = 'http://www.w3.org/2000/svg';

  function shippingAccessibility(root) {
    const input = root.querySelector('#formKokfyShipping #zipcode');
    if (input && !input.hasAttribute('aria-label')) input.setAttribute('aria-label', root.dataset.psPostalCode);
    root.querySelectorAll('[data-ps-shipping-panel]').forEach((panel) => {
      const widget = panel.querySelector('[data-ps-shipping-widget]');
      const ready = Boolean(widget?.querySelector('input:not([type="hidden"]), iframe'));
      const calculator = panel.querySelector('details');
      const fallback = panel.querySelector('[data-ps-shipping-fallback]');
      if (calculator) calculator.hidden = !ready;
      if (fallback) fallback.hidden = ready;
    });
  }

  function reviews(root) {
    const summary = root.querySelector('#auto-bk-star-section');
    const link = summary?.querySelector('a[href="#bk-reviews-section"]');
    if (!link) return;
    const target = root.querySelector('[data-ps-review-target]');
    if (target && summary.parentElement !== target) target.append(summary);
    const value = link.querySelector('.tw-font-bold');
    const count = link.querySelector('a > div > span');
    const average = Number(value?.textContent.trim().replace(',', '.'));
    const total = Number(count?.textContent.replace(/\D/g, ''));
    const valid = Number.isFinite(average) && average > 0 && average <= 5 && total > 0;
    summary.hidden = !valid;
    if (!valid) return;
    const signature = `${average}:${total}`;
    if (summary.dataset.psRating === signature) return;
    summary.dataset.psRating = signature;
    const ratingText = decimal.format(average);
    value.textContent = ratingText;
    count.textContent = `${total} ${root.dataset.psReviews}`;
    link.setAttribute('aria-label', `${ratingText} ${root.dataset.psReviewLabel}. ${total} ${root.dataset.psReviews}.`);
    link.querySelectorAll('svg').forEach((svg, index) => {
      const path = svg.querySelector('path');
      if (!path || index > 4) return;
      const fill = Math.max(0, Math.min(1, average - index)) * 100;
      const id = `PsReview-${root.dataset.sectionId}-${index}`;
      let defs = svg.querySelector('defs[data-ps-rating-fill]');
      if (!defs) {
        defs = document.createElementNS(svgNs, 'defs');
        defs.dataset.psRatingFill = '';
        svg.prepend(defs);
      }
      const gradient = document.createElementNS(svgNs, 'linearGradient');
      gradient.id = id;
      for (const [offset, color] of [[0, 'var(--ps-review-star-color, #111)'], [fill, 'var(--ps-review-star-color, #111)'], [fill, 'var(--ps-review-empty, #fff)'], [100, 'var(--ps-review-empty, #fff)']]) {
        const stop = document.createElementNS(svgNs, 'stop');
        stop.setAttribute('offset', `${offset}%`);
        stop.style.stopColor = color;
        gradient.append(stop);
      }
      defs.replaceChildren(gradient);
      path.setAttribute('fill', `url(#${id})`);
      path.style.stroke = 'var(--ps-review-star-color, #111)';
      path.setAttribute('stroke-width', '1');
      svg.setAttribute('aria-hidden', 'true');
    });
  }

  function groups(root) {
    return Array.from(root.querySelectorAll('variant-options .wt-product__option'));
  }

  function missing(root) {
    return groups(root).find(group => !group.querySelector('input[type="radio"]:checked'));
  }

  function updateOptions(root) {
    const options = groups(root);
    options.forEach(group => {
      const checked = group.querySelector('input:checked');
      const title = group.querySelector('.wt-product__option__title .value');
      if (!checked && title?.textContent) title.textContent = '';
      group.querySelectorAll('input[type="radio"]').forEach(input => {
        const unavailable = input.classList.contains('disabled') ? ` — ${root.dataset.psUnavailable}` : '';
        input.setAttribute('aria-label', `${input.name}: ${input.value}${unavailable}`);
      });
    });
    const incomplete = missing(root);
    const button = root.querySelector('.product-form__submit');
    if (incomplete && button && root.dataset.psAvailable === 'true') {
      button.disabled = false;
      button.querySelector('.wt-btn__label span').textContent = root.dataset.psAddLabel;
      root.querySelector('[data-block-id="price"]')?.classList.remove('visibility-hidden');
    }
    const error = root.querySelector('.ps-pdp-standard__variant-error');
    if (!incomplete && error) {
      error.hidden = true;
      root.querySelectorAll('[aria-invalid="true"]').forEach(el => el.removeAttribute('aria-invalid'));
    }
  }

  function cashback(root) {
    const controller = new AbortController();
    root.querySelectorAll('[data-ps-cashback-float]').forEach(widget => {
      const key = `ps-cashback-dismissed:${widget.dataset.productId}`;
      const remember = widget.dataset.remember === 'true' && widget.dataset.designMode !== 'true';
      if (remember) {
        try { widget.hidden = sessionStorage.getItem(key) === 'true'; } catch {}
      }
      widget.querySelector('[data-ps-cashback-dismiss]')?.addEventListener('click', () => {
        widget.hidden = true;
        if (remember) {
          try { sessionStorage.setItem(key, 'true'); } catch {}
        }
        root.querySelector('.wt-product__sticky-buy__button')?.focus({ preventScroll: true });
      }, { signal: controller.signal });
      widget.querySelector('[data-ps-cashback-open]')?.addEventListener('click', event => {
        const panel = document.getElementById(event.currentTarget.getAttribute('aria-controls'));
        if (!panel) return;
        event.preventDefault();
        panel.open = true;
        panel.querySelector('summary')?.focus({ preventScroll: true });
        panel.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }, { signal: controller.signal });
    });
    root.addEventListener('ps:cleanup', () => controller.abort(), { once: true });
  }

  function init(root) {
    if (initialized.has(root)) return;
    initialized.add(root);
    groups(root).forEach(group => {
      const inputs = group.querySelectorAll('input[type="radio"]');
      if (inputs.length === 1 && /^(cor|color|colour)$/i.test(inputs[0].name)) inputs[0].checked = true;
    });
    reviews(root);
    shippingAccessibility(root);
    updateOptions(root);
    cashback(root);
    root.querySelectorAll('[data-ps-description]').forEach(preview => {
      const content = preview.querySelector('.ps-pdp-description__content');
      const button = preview.querySelector('[data-ps-description-toggle]');
      const desktop = matchMedia('(min-width: 900px)');
      const isFullDesktop = () => desktop.matches && preview.dataset.desktopFull === 'true';
      const update = expanded => {
        preview.dataset.collapsed = String(!expanded && !isFullDesktop());
        button.setAttribute('aria-expanded', String(expanded));
        const suffix = button.dataset.textOnly === 'true' ? '' : expanded ? ' −' : ' +';
        button.textContent = (expanded ? button.dataset.less : button.dataset.more) + suffix;
      };
      update(false);
      let expanded = false;
      const measure = () => {
        const styles = getComputedStyle(preview);
        const variable = desktop.matches ? '--ps-description-height' : '--ps-description-mobile-height';
        const limit = parseFloat(styles.getPropertyValue(variable)) || 180;
        const overflows = content.scrollHeight > limit + 1;
        button.hidden = isFullDesktop() || !overflows;
        preview.dataset.collapsed = String(!isFullDesktop() && overflows && !expanded);
      };
      const observer = new ResizeObserver(measure);
      observer.observe(content);
      desktop.addEventListener('change', measure);
      measure();
      button.addEventListener('click', () => {
        expanded = button.getAttribute('aria-expanded') !== 'true';
        update(expanded);
      });
      content.addEventListener('focusin', () => { expanded = true; update(true); });
      root.addEventListener('ps:cleanup', () => { observer.disconnect(); desktop.removeEventListener('change', measure); }, { once: true });
    });
    const info = root.querySelector('.wt-product__info');
    const observer = new MutationObserver(() => {
      reviews(root);
      shippingAccessibility(root);
    });
    observer.observe(info, { childList: true, subtree: true, characterData: true });
    root.addEventListener('ps:cleanup', () => observer.disconnect(), { once: true });
    root.addEventListener('variantChangeEnd', () => updateOptions(root));
    root.addEventListener('submit', event => {
      if (!event.target.matches('form[data-type="add-to-cart-form"]')) return;
      const group = missing(root);
      if (!group) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const input = group.querySelector('input[type="radio"]');
      const error = root.querySelector('.ps-pdp-standard__variant-error');
      error.textContent = /^(tamanho|size)$/i.test(input?.name || '') ? root.dataset.psSizeError : root.dataset.psOptionError;
      error.hidden = false;
      group.querySelector('.wt-product__option__body').before(error);
      group.querySelector('fieldset').setAttribute('aria-describedby', error.id);
      input.setAttribute('aria-invalid', 'true');
      input.focus({ preventScroll: true });
      group.scrollIntoView({ block: 'center', behavior: 'instant' });
    }, true);
  }

  document.querySelectorAll(selector).forEach(init);
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll(selector).forEach(root => root.dispatchEvent(new Event('ps:cleanup'))));
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll(selector).forEach(init));
  if (typeof subscribe === 'function') subscribe(PUB_SUB_EVENTS.variantChange, () => document.querySelectorAll(selector).forEach(updateOptions));
})();

